import {
  convertQty,
  normalizeComparableUom,
  type StockProjectorEvent,
} from '@trackeat/shared';
import {
  isPhysicalPurchaseReceiptPayload,
  selectActivePhysicalPurchaseReceipts,
} from '../physicalPurchaseReceipts.js';

/** Numerical tolerance used only to recognise an empty projected lot. */
export const HISTORICAL_LOT_EPSILON = 1e-9;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const STOCK_RECONCILIATION_MARKER_PREFIX = 'stock-reconciliation:v1:';
const CONVERSION_SOURCES = new Set([
  'identity',
  'metric',
  'document_pack',
  'document_weight',
  'supplier_alias',
  'user_confirmed',
  'carried',
]);

/**
 * Narrow structural view of a projected lot. `StockBalance` is assignable to
 * this type, while keeping this selector independent from a particular page.
 */
export interface HistoricalLotBalance {
  tenantId: string;
  businessId: string | null;
  foodId: string;
  batchLot: string | null;
  uom: string;
  onHandQty: number;
  /** A recount must never consume or overwrite a shipment reservation. */
  reservedQty?: number;
  closed: boolean;
  eventCount: number;
  lastEventId: string | null;
  /** Present only in projections which partition stock by storage unit. */
  storageUnitId?: string | null;
}

/**
 * Conversion evidence captured on the original document-backed receipt.
 * `factorPerCarton` is expressed in the balance's unit, never inferred from
 * a product name or a current catalogue setting.
 */
export interface HistoricalLotCartonConversion {
  sourceUom: 'ct';
  sourceCartons: number | null;
  sourceStockUom: string;
  balanceUom: string;
  factorPerCarton: number;
}

export interface HistoricalLotSource {
  documentId: string;
  /** Authoritative immutable event id, assigned only after server acceptance. */
  sourceEventId: string;
  /** Local client UUID retained for compare-and-swap evidence. */
  sourceEventClientUuid: string | null;
  event: HistoricalLotSourceEvent;
  cartonConversion: HistoricalLotCartonConversion | null;
}

/**
 * `useEvents` rows add `server_id` to the shared projector event shape. A
 * historical reconciliation may only name an authoritative server event, so a
 * missing `server_id` is deliberately ineligible even if the local client id
 * is present.
 */
export interface HistoricalLotSourceEvent extends StockProjectorEvent {
  server_id?: string | null;
  client_ts?: string | null;
  rev?: number | null;
}

export interface HistoricalLotExpectedBalance {
  qty: number;
  event_count: number;
  last_event_client_uuid: string | null;
  closed: boolean;
}

export interface HistoricalLotCandidate {
  foodId: string;
  batchLot: string;
  documentId: string;
  supplierId: string | null;
  expirationDate: string | null;
  /** The authoritative `events.server_id` of the original receipt. */
  sourceEventId: string;
  sourceEventClientUuid: string | null;
  sourceUom: string | null;
  conversionFactor: number | null;
  conversionSource: string | null;
  /** Whether the source already uses the current V1 envelope or is a retained DDT history. */
  recoveryPath: 'canonical' | 'legacy_document';
  expectedBalance: HistoricalLotExpectedBalance;
  balance: HistoricalLotBalance;
  source: HistoricalLotSource;
}

function cleanText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const clean = value.trim();
  return clean || null;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function isUuid(value: string | null): value is string {
  return value !== null && UUID_RE.test(value);
}

function confirmedServerRevision(event: HistoricalLotSourceEvent): number | null {
  if (
    typeof event.server_rev === 'number'
    && Number.isInteger(event.server_rev)
    && event.server_rev > 0
  ) {
    return event.server_rev;
  }
  if (
    typeof event.rev === 'number'
    && Number.isInteger(event.rev)
    && event.rev > 0
    && cleanText(event.server_id) !== null
  ) {
    return event.rev;
  }
  return null;
}

function isRawUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

function isNullableUuid(value: unknown): boolean {
  return value === null || isRawUuid(value);
}

function isNullableNonNegativeNumber(value: unknown): boolean {
  return value === null || (typeof value === 'number' && Number.isFinite(value) && value >= 0);
}

function isIsoDateOrNull(value: unknown): boolean {
  if (value === null) return true;
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function sourceEventClientUuid(event: HistoricalLotSourceEvent): string | null {
  return cleanText(event.id) ?? cleanText(event.client_uuid);
}

function payloadOf(event: HistoricalLotSourceEvent): Readonly<Record<string, unknown>> {
  return event.payload ?? {};
}

function isActiveBusiness(
  event: HistoricalLotSourceEvent,
  activeBusinessId: string | null,
): boolean {
  if (activeBusinessId === null || event.business_id !== activeBusinessId) {
    return false;
  }
  const payload = payloadOf(event);
  const rawPayloadBusinessId = payload.business_id;
  // This must mirror the server RPC: the row-level business coordinate is
  // mandatory; the legacy payload coordinate may be omitted/null, but when
  // present it must agree exactly. A sole-site projection fallback is not
  // enough evidence to make an append-only correction eligible.
  return rawPayloadBusinessId == null || rawPayloadBusinessId === activeBusinessId;
}

function isDocumentBackedReceipt(event: HistoricalLotSourceEvent): boolean {
  const payload = payloadOf(event);
  if (
    payload.historical_recount === true
    || payload.reopen_lot === true
    || payload.inventory_delta != null
    || payload.inventory_system_qty != null
    || payload.inventory_counted_qty != null
    || payload.inventory_counted_at != null
    || cleanText(payload.lot_action)?.toLowerCase() === 'recorded_only'
  ) {
    return false;
  }

  if (payload.movement_class === 'receive' && payload.economic_effect === 'purchase') {
    return true;
  }

  // Before StockCommand V1, a DDT/fattura receipt stored the physical
  // `qty/uom` directly. It is still an immutable, document-backed purchase;
  // it must not disappear merely because it lacks the later envelope.
  if (
    Object.prototype.hasOwnProperty.call(payload, 'movement_class')
    || Object.prototype.hasOwnProperty.call(payload, 'economic_effect')
    || Object.prototype.hasOwnProperty.call(payload, 'command_id')
  ) {
    return false;
  }

  const reason = cleanText(payload.reason)?.toLowerCase();
  return isPhysicalPurchaseReceiptPayload(payload)
    && reason !== 'inventory_adjustment'
    && reason !== 'historical_recount'
    && reason !== 'consultant_historical_recount';
}

/**
 * Keep the actionability preflight in lock-step with the server source
 * contract. A historical card may be informative, but a source without every
 * canonical coordinate would be a guaranteed terminal outbox conflict.
 */
function hasServerEligibleSourceCoordinates(
  event: HistoricalLotSourceEvent,
  balance: HistoricalLotBalance,
): boolean {
  const payload = payloadOf(event);
  const sourceUom = cleanText(payload.source_uom);
  const stockUom = cleanText(payload.stock_uom);
  const sourceQty = finiteNumber(payload.source_qty);
  const stockQty = finiteNumber(payload.stock_qty);
  const conversionFactor = finiteNumber(payload.conversion_factor);
  const conversionSource = payload.conversion_source;

  if (
    !isRawUuid(payload.food_id)
    || !isRawUuid(payload.document_id)
    || !isNullableUuid(payload.supplier_id)
    || !isNullableUuid(payload.storage_unit_id)
    || !isIsoDateOrNull(payload.expiration_date)
    || sourceUom === null
    || stockUom === null
    || sourceUom.length > 10
    || stockUom.length > 10
    || normalizeComparableUom(sourceUom) === ''
    || normalizeComparableUom(stockUom) === ''
    || normalizeComparableUom(stockUom) !== normalizeComparableUom(balance.uom)
    || sourceQty === null
    || sourceQty <= 0
    || stockQty === null
    || stockQty <= 0
    || conversionFactor === null
    || conversionFactor <= 0
    || typeof conversionSource !== 'string'
    || !CONVERSION_SOURCES.has(conversionSource)
    || !isNullableNonNegativeNumber(payload.source_unit_price)
    || !isNullableNonNegativeNumber(payload.source_line_total)
    || !isNullableNonNegativeNumber(payload.stock_unit_cost)
    || typeof payload.source_currency !== 'string'
    || !/^[A-Z]{3}$/.test(payload.source_currency)
  ) {
    return false;
  }

  const expectedStockQty = sourceQty * conversionFactor;
  return Math.abs(expectedStockQty - stockQty)
    <= 1e-12 * Math.max(1, Math.abs(expectedStockQty), Math.abs(stockQty));
}

/**
 * A confirmed pre-V1 DDT/fattura keeps its original `qty/uom` coordinates.
 * The server normalizes only this authoritative source in-memory before
 * appending the recount; the historic event itself is never rewritten.
 */
function hasLegacyDocumentCoordinates(
  event: HistoricalLotSourceEvent,
  balance: HistoricalLotBalance,
  cutoverRevision: number,
): boolean {
  const payload = payloadOf(event);
  const hasNoSemanticEnvelope = !Object.prototype.hasOwnProperty.call(payload, 'movement_class')
    && !Object.prototype.hasOwnProperty.call(payload, 'economic_effect')
    && !Object.prototype.hasOwnProperty.call(payload, 'command_id');
  if (!hasNoSemanticEnvelope && isAtOrAfterStockUomCutover(event, cutoverRevision)) {
    return false;
  }
  const sourceUom = cleanText(payload.source_uom) ?? cleanText(payload.uom);
  const sourceQty = finiteNumber(payload.source_qty) ?? finiteNumber(payload.qty);
  const sourceFactor = finiteNumber(payload.conversion_factor) ?? 1;
  const stockUom = cleanText(payload.stock_uom) ?? cleanText(payload.uom);
  const stockQty = finiteNumber(payload.stock_qty) ?? finiteNumber(payload.qty);

  return isUuid(cleanText(payload.document_id))
    && isUuid(cleanText(payload.food_id))
    && sourceUom !== null
    && stockUom !== null
    && normalizeComparableUom(sourceUom) !== ''
    && normalizeComparableUom(stockUom) !== ''
    && normalizeComparableUom(stockUom) === normalizeComparableUom(balance.uom)
    && sourceQty !== null
    && sourceQty > 0
    && stockQty !== null
    && stockQty > 0
    && sourceFactor > 0
    && Math.abs(sourceQty * sourceFactor - stockQty)
      <= 1e-12 * Math.max(1, Math.abs(sourceQty * sourceFactor), Math.abs(stockQty));
}

function isEmptyOrClosed(balance: HistoricalLotBalance): boolean {
  return balance.closed || Math.abs(balance.onHandQty) <= HISTORICAL_LOT_EPSILON;
}

function matchesBalance(
  event: HistoricalLotSourceEvent,
  balance: HistoricalLotBalance,
): boolean {
  const payload = payloadOf(event);
  const balanceLot = cleanText(balance.batchLot);
  if (balanceLot === null) return false;
  if (cleanText(payload.food_id) !== cleanText(balance.foodId)) return false;
  if (cleanText(payload.batch_lot) !== balanceLot) return false;

  const storageUnitId = cleanText(balance.storageUnitId);
  return storageUnitId === null
    || cleanText(payload.storage_unit_id) === storageUnitId;
}

/**
 * The server's historical-recount locator is business + food + batch. Storage
 * is provenance metadata, not a separate recoverable balance, so an exact
 * cancelled receipt in any storage coordinate makes the whole locator
 * ineligible until it is resolved through the dedicated document flow.
 */
function historicalLotLocatorKey(input: {
  tenantId: string;
  businessId: string;
  foodId: string;
  batchLot: string;
}): string {
  return JSON.stringify([
    input.tenantId,
    input.businessId,
    input.foodId,
    input.batchLot,
  ]);
}

/**
 * A normal source must be business-scoped before it is actionable. For the
 * neutralization deny-list only, retain a legacy unscoped receipt whose
 * payload is compatible with the active business. The server may bind that
 * legacy pair when the tenant is single-site; hiding an ambiguous recovery in
 * a multi-site cache is safer than placing a terminal RPC conflict in queue.
 */
function locatorKeyForNeutralizedEvent(
  event: HistoricalLotSourceEvent,
  activeBusinessId: string | null,
): string | null {
  const payload = payloadOf(event);
  const legacyCompatible = activeBusinessId !== null
    && event.business_id == null
    && (payload.business_id == null || payload.business_id === activeBusinessId);
  if (!isActiveBusiness(event, activeBusinessId) && !legacyCompatible) {
    return null;
  }
  const tenantId = cleanText(event.tenant_id);
  const foodId = cleanText(payload.food_id);
  const batchLot = cleanText(payload.batch_lot);
  if (tenantId === null || foodId === null || batchLot === null || activeBusinessId === null) {
    return null;
  }
  return historicalLotLocatorKey({
    tenantId,
    businessId: activeBusinessId,
    foodId,
    batchLot,
  });
}

function locatorKeyForBalance(
  balance: HistoricalLotBalance,
  activeBusinessId: string | null,
): string | null {
  const tenantId = cleanText(balance.tenantId);
  const foodId = cleanText(balance.foodId);
  const batchLot = cleanText(balance.batchLot);
  if (tenantId === null || foodId === null || batchLot === null || activeBusinessId === null) {
    return null;
  }
  return historicalLotLocatorKey({
    tenantId,
    businessId: activeBusinessId,
    foodId,
    batchLot,
  });
}

/**
 * Mirrors `stock_uom_cutover_rev_v1` for the only purpose of withholding a
 * card which would be rejected by the authoritative RPC. It intentionally
 * uses confirmed local revisions only: a queued marker has no server cutover
 * yet, while an incompletely pulled marker must never be guessed from time.
 */
function stockUomCutoverRevision(
  events: readonly HistoricalLotSourceEvent[],
  tenantId: string,
  foodId: string,
): number {
  const isScoped = (event: HistoricalLotSourceEvent): boolean =>
    event.kind === 'stock_in'
    && event.tenant_id === tenantId
    && payloadOf(event).food_id === foodId;
  const isRebaseSnapshot = (event: HistoricalLotSourceEvent): boolean => {
    const payload = payloadOf(event);
    return isScoped(event)
      && confirmedServerRevision(event) !== null
      && payload.movement_class === 'uom_rebase'
      && payload.uom_rebase_snapshot === true
      && typeof payload.command_id === 'string';
  };

  let latestRebaseCommand: { commandId: string; revision: number } | null = null;
  for (const event of events) {
    if (!isRebaseSnapshot(event)) continue;
    const revision = confirmedServerRevision(event);
    const commandId = payloadOf(event).command_id;
    if (revision === null || typeof commandId !== 'string') continue;
    if (latestRebaseCommand === null || revision > latestRebaseCommand.revision) {
      latestRebaseCommand = { commandId, revision };
    }
  }

  let rebaseBoundary = 0;
  if (latestRebaseCommand !== null) {
    for (const event of events) {
      if (!isRebaseSnapshot(event)) continue;
      if (payloadOf(event).command_id !== latestRebaseCommand.commandId) continue;
      const revision = confirmedServerRevision(event);
      if (revision !== null) {
        rebaseBoundary = rebaseBoundary === 0
          ? revision
          : Math.min(rebaseBoundary, revision);
      }
    }
  }

  let reconciliationBoundary = 0;
  for (const event of events) {
    if (!isScoped(event)) continue;
    const revision = confirmedServerRevision(event);
    const payload = payloadOf(event);
    const reference = cleanText(payload.source_line_ref);
    const usesStockCoordinates = typeof payload.stock_qty === 'number'
      && Number.isFinite(payload.stock_qty)
      && cleanText(payload.stock_uom) !== null
      && normalizeComparableUom(cleanText(payload.stock_uom)) !== ''
      && payload.stock_qty === 0;
    const usesLegacyCoordinates = typeof payload.qty === 'number'
      && Number.isFinite(payload.qty)
      && cleanText(payload.uom) !== null
      && normalizeComparableUom(cleanText(payload.uom)) !== ''
      && payload.qty === 0;
    const markerId = reference?.slice(STOCK_RECONCILIATION_MARKER_PREFIX.length) ?? '';
    if (
      revision === null
      || payload.movement_class !== 'lot_state'
      || payload.economic_effect !== 'stock_only'
      || payload.lot_state_action !== 'update_metadata'
      || !reference?.startsWith(STOCK_RECONCILIATION_MARKER_PREFIX)
      || !UUID_RE.test(markerId)
      || (!usesStockCoordinates && !usesLegacyCoordinates)
    ) {
      continue;
    }
    reconciliationBoundary = Math.max(reconciliationBoundary, revision);
  }

  return Math.max(rebaseBoundary, reconciliationBoundary);
}

function isAtOrAfterStockUomCutover(
  event: HistoricalLotSourceEvent,
  cutover: number,
): boolean {
  if (cutover === 0) return true;
  const sourceRevision = confirmedServerRevision(event);
  return sourceRevision !== null && sourceRevision >= cutover;
}

function readCartonConversion(
  payload: Readonly<Record<string, unknown>>,
  balanceUom: string,
): HistoricalLotCartonConversion | null {
  if (normalizeComparableUom(cleanText(payload.source_uom)) !== 'ct') return null;

  const rawFactor = finiteNumber(payload.conversion_factor);
  const sourceStockUom = cleanText(payload.stock_uom) ?? cleanText(payload.uom);
  const cleanBalanceUom = cleanText(balanceUom);
  if (
    rawFactor === null
    || rawFactor <= 0
    || sourceStockUom === null
    || cleanBalanceUom === null
  ) {
    return null;
  }

  const factorPerCarton = convertQty(rawFactor, sourceStockUom, cleanBalanceUom);
  if (factorPerCarton === null || !Number.isFinite(factorPerCarton) || factorPerCarton <= 0) {
    return null;
  }

  const sourceQty = finiteNumber(payload.source_qty);
  return {
    sourceUom: 'ct',
    sourceCartons: sourceQty !== null && sourceQty > 0 ? sourceQty : null,
    sourceStockUom,
    balanceUom: cleanBalanceUom,
    factorPerCarton,
  };
}

function compareCandidate(
  left: HistoricalLotCandidate,
  right: HistoricalLotCandidate,
): number {
  return left.balance.foodId.localeCompare(right.balance.foodId)
    || (left.balance.batchLot ?? '').localeCompare(right.balance.batchLot ?? '')
    || left.source.documentId.localeCompare(right.source.documentId)
    || left.sourceEventId.localeCompare(right.sourceEventId);
}

/**
 * Returns every exact, document-backed original receipt for a closed or empty
 * lot in the active business. The result deliberately retains distinct source
 * events even when they point to the same document: callers must present that
 * ambiguity to the consultant instead of silently picking one event.
 */
export function selectHistoricalLotCandidates(input: {
  balances: readonly HistoricalLotBalance[];
  stockInEvents: readonly HistoricalLotSourceEvent[];
  /** Needed to exclude a document receipt later neutralized by an exact reverse. */
  stockOutEvents: readonly HistoricalLotSourceEvent[];
  activeBusinessId: string | null;
}): HistoricalLotCandidate[] {
  const candidates: HistoricalLotCandidate[] = [];
  // Source provenance is not active merely because it is a `stock_in`: an
  // explicit, exact later cancellation/reversal must remove the original DDT
  // from this recovery flow. Reuse the shared food-cost/traceability
  // classifier rather than maintaining a second weaker interpretation.
  const activeReceipts = selectActivePhysicalPurchaseReceipts(
    input.stockInEvents,
    input.stockOutEvents,
  ) as readonly HistoricalLotSourceEvent[];
  const activeReceiptSet = new Set(activeReceipts);
  const neutralizedLocatorKeys = new Set(
    input.stockInEvents.flatMap((event) => {
      if (
        activeReceiptSet.has(event)
        || event.kind !== 'stock_in'
        || !isPhysicalPurchaseReceiptPayload(event.payload)
      ) {
        return [];
      }
      const locatorKey = locatorKeyForNeutralizedEvent(
        event,
        input.activeBusinessId,
      );
      return locatorKey === null ? [] : [locatorKey];
    }),
  );
  const cutoverRevisionByFood = new Map<string, number>();
  const cutoverForBalance = (balance: HistoricalLotBalance): number => {
    const key = JSON.stringify([balance.tenantId, balance.foodId]);
    const cached = cutoverRevisionByFood.get(key);
    if (cached !== undefined) return cached;
    const cutover = stockUomCutoverRevision(
      input.stockInEvents,
      balance.tenantId,
      balance.foodId,
    );
    cutoverRevisionByFood.set(key, cutover);
    return cutover;
  };

  for (const balance of input.balances) {
    if (
      balance.businessId !== input.activeBusinessId
      || !isEmptyOrClosed(balance)
      || cleanText(balance.tenantId) === null
      || cleanText(balance.foodId) === null
      || cleanText(balance.batchLot) === null
    ) {
      continue;
    }

    const balanceLocatorKey = locatorKeyForBalance(
      balance,
      input.activeBusinessId,
    );
    if (
      balanceLocatorKey === null
      || neutralizedLocatorKeys.has(balanceLocatorKey)
    ) {
      continue;
    }

    for (const event of activeReceipts) {
      if (
        event.kind !== 'stock_in'
        || event.tenant_id !== balance.tenantId
        || !isActiveBusiness(
          event,
          input.activeBusinessId,
        )
        || !isDocumentBackedReceipt(event)
        || !matchesBalance(event, balance)
      ) {
        continue;
      }

      const cutoverRevision = cutoverForBalance(balance);
      const canonicalSource = hasServerEligibleSourceCoordinates(event, balance)
        && isAtOrAfterStockUomCutover(event, cutoverRevision);
      const historicalDocumentSource = hasLegacyDocumentCoordinates(
        event,
        balance,
        cutoverRevision,
      );
      if (!canonicalSource && !historicalDocumentSource) continue;

      const payload = payloadOf(event);
      const documentId = cleanText(payload.document_id);
      const authoritativeSourceEventId = cleanText(event.server_id);
      if (documentId === null || !isUuid(authoritativeSourceEventId)) continue;

      const clientUuid = sourceEventClientUuid(event);
      const sourceUom = cleanText(payload.source_uom) ?? cleanText(payload.uom);
      const conversionFactor = finiteNumber(payload.conversion_factor) ?? 1;
      const conversionSource = cleanText(payload.conversion_source) ?? 'identity';
      const batchLot = cleanText(balance.batchLot);
      // Guarded above, retained to keep the flat page-facing shape non-null.
      if (batchLot === null) continue;

      candidates.push({
        foodId: balance.foodId,
        batchLot,
        documentId,
        supplierId: cleanText(payload.supplier_id),
        expirationDate: cleanText(payload.expiration_date),
        sourceEventId: authoritativeSourceEventId,
        sourceEventClientUuid: clientUuid,
        sourceUom,
        conversionFactor,
        conversionSource,
        recoveryPath: canonicalSource ? 'canonical' : 'legacy_document',
        expectedBalance: {
          qty: balance.onHandQty,
          event_count: balance.eventCount,
          last_event_client_uuid: cleanText(balance.lastEventId),
          closed: balance.closed,
        },
        balance,
        source: {
          documentId,
          sourceEventId: authoritativeSourceEventId,
          sourceEventClientUuid: clientUuid,
          event,
          cartonConversion: readCartonConversion(payload, balance.uom),
        },
      });
    }
  }

  return candidates.sort(compareCandidate);
}
